import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import {
  AvatarName,
  Badge,
  DataCard,
  Input,
  PageTitle,
  Select,
  Spinner,
  Td,
  Th
} from "../../components/ui";

export default function TrainerStudentsPage({ user, setToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState("10");

  async function load() {
    setLoading(true);

    try {
      const res = await api.getStudents(user.email);

      if (res.success) {
        setRows(res.data || []);
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to load students"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((student) =>
      [
        student.name,
        student.studentId,
        student.sessions,
        student.schedule,
        student.trainer
      ]
        .join(" ")
        .toLowerCase()
        .includes(query.toLowerCase())
    );
  }, [rows, query]);

  const visible = limit === "All" ? filtered : filtered.slice(0, Number(limit));

  return (
    <>
      <PageTitle
        title="Student List"
        subtitle="Manage and monitor student enrollment and status."
        action={
          <div className="flex gap-2">
            <Input
              placeholder="Search students..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <Select
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="w-32"
            >
              <option>10</option>
              <option>20</option>
              <option>50</option>
              <option>All</option>
            </Select>
          </div>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <DataCard>
          <table className="w-full min-w-[720px] whitespace-nowrap text-left">
            <thead>
              <tr>
                {[
                  "STUDENT ID",
                  "NAME",
                  "PERIODE",
                  "SCHEDULE",
                  "SESSIONS",
                  "TRAINER",
                  "WA",
                  "EMAIL"
                ].map((h) => (
                  <Th key={h}>{h}</Th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visible.map((s) => (
                <tr
                  key={s.rowNumber || s.studentId}
                  className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-primary/5"
                >
                  <Td mono primary>
                    {s.studentId}
                  </Td>

                  <Td>
                    <AvatarName name={s.name} />
                  </Td>

                  <Td>
                    <Badge>{s.periode}</Badge>
                  </Td>

                  <Td>{s.schedule}</Td>
                  <Td>{s.sessions}</Td>
                  <Td>{s.trainer}</Td>
                  <Td>{s.wa}</Td>
                  <Td>{s.email}</Td>
                </tr>
              ))}

              {!visible.length && (
                <tr>
                  <Td colSpan="8">No students found.</Td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="border-t border-slate-200 bg-slate-50 p-4 text-center text-sm font-bold text-slate-500 dark:border-slate-800 dark:bg-slate-800/40">
            Showing {visible.length} of {filtered.length} students
          </div>
        </DataCard>
      )}
    </>
  );
}